import React, { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, Monitor, RefreshCw, Smartphone, Tablet } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const DEVICES = [
    { key: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
    { key: "tablet", label: "Tablet", icon: Tablet, width: "834px" },
    { key: "mobile", label: "Mobile", icon: Smartphone, width: "414px" },
];

/**
 * Renders a real public page in an iframe and streams the unsaved draft into it, so the CRM
 * always previews exactly what a visitor would see. Edits come back on `editMessage`.
 */
export const LivePreviewFrame = ({ path, payload, message, editMessage, onEdit, title = "Live preview",
    liveUrl = "", note = "", height = "72vh", testId = "live-preview" }) => {
    const frame = useRef(null);
    const [device, setDevice] = useState("desktop");
    const [ready, setReady] = useState(false);
    const [nonce, setNonce] = useState(0);
    const data = useMemo(() => payload, [payload]);

    useEffect(() => {
        const onMessage = (e) => {
            if (e.data?.type === `${message}-ready`) setReady(true);
            if (editMessage && e.data?.type === editMessage && onEdit) onEdit(e.data.payload || {});
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [message, editMessage, onEdit]);

    useEffect(() => {
        if (!ready) return undefined;
        const timer = setTimeout(() => {
            frame.current?.contentWindow?.postMessage({ type: message, payload: data }, "*");
        }, 400);
        return () => clearTimeout(timer);
    }, [data, ready, nonce, message]);

    const current = DEVICES.find((d) => d.key === device);

    return (
        <div className="border border-white/10 bg-black/30" data-testid={testId}>
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2">
                <div className="flex items-center gap-1">
                    {DEVICES.map((d) => (
                        <button key={d.key} type="button" onClick={() => setDevice(d.key)}
                            data-testid={`preview-device-${d.key}`} title={d.label}
                            className={`p-2 transition-colors ${device === d.key ? "text-gold" : "text-zinc-500 hover:text-white"}`}>
                            <d.icon className="w-4 h-4" />
                        </button>
                    ))}
                    <span className="mono text-[9px] tracking-[0.16em] text-zinc-500 ml-2">
                        LIVE PREVIEW · {current.label.toUpperCase()}{note ? ` · ${note}` : ""}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => { setReady(false); setNonce((n) => n + 1); }}
                        data-testid="preview-refresh" title="Reload preview"
                        className="p-2 text-zinc-500 hover:text-white transition-colors">
                        <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    {liveUrl && (
                        <a href={liveUrl} target="_blank" rel="noreferrer"
                            data-testid="preview-open-tab" title="Open the live page"
                            className="p-2 text-zinc-500 hover:text-gold transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
            </div>
            <div className="p-3 flex justify-center overflow-auto bg-[#0b0b0d]">
                <iframe key={nonce} ref={frame} title={title} src={`${BASE}${path}`}
                    style={{ width: current.width, height, maxWidth: "100%" }}
                    className="bg-white border border-white/10" />
            </div>
            {!ready && (
                <div className="px-3 pb-3 mono text-[9px] tracking-[0.16em] text-zinc-500">
                    CONNECTING TO THE PREVIEW…
                </div>
            )}
        </div>
    );
};

export default LivePreviewFrame;
